import { useEffect, useState } from 'react'
import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut
} from 'firebase/auth'
import { httpsCallable, getFunctions } from 'firebase/functions'

// Guard so the one-time email sign-in code is only ever consumed once,
// regardless of re-renders or React StrictMode double-invoking effects.
// An oobCode is single-use; a second signInWithEmailLink call 400s with
// INVALID_OOB_CODE.
let emailLinkSignInAttempted = false

const useAuthHook = () => {
  const [user, setUser] = useState()
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState()

  useEffect(() => {
    onAuthStateChanged(getAuth(), (user) => {
      setLoading(false)
      setUser(user)
    })
  }, [])

  const loginWithGoogle = () => {
    const provider = new GoogleAuthProvider()
    signInWithPopup(getAuth(), provider).then((result) => {
      setAuthError()
      setUser(result.user)
    }).catch(err => {
      if (err.code === 'auth/error-code:-47') {
        setAuthError('User is not authorized to access this application.')
      } else {
        setAuthError('Unknown issue occurred during login.')
      }
    })
  }

  const loginWithEmail = async (email, password) => {
    setAuthError()
    try {
      const result = await signInWithEmailAndPassword(getAuth(), email, password)
      setUser(result.user)
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setAuthError('Invalid email or password.')
      } else if (err.code === 'auth/too-many-requests') {
        setAuthError('Too many failed attempts. Please try again later.')
      } else {
        setAuthError('Login failed. Please try again.')
      }
    }
  }

  const register = async (email, password) => {
    setAuthError()
    try {
      const result = await createUserWithEmailAndPassword(getAuth(), email, password)
      setUser(result.user)
      return true
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setAuthError('An account with this email already exists.')
      } else if (err.code === 'auth/weak-password') {
        setAuthError('Password must be at least 6 characters.')
      } else if (err.code === 'auth/error-code:-47') {
        setAuthError('This email is not authorized to access this application.')
      } else {
        setAuthError('Registration failed. Please try again.')
      }
      return false
    }
  }

  const resetPassword = async (email) => {
    setAuthError()
    try {
      await sendPasswordResetEmail(getAuth(), email)
      return true
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setAuthError('No account found with this email.')
      } else {
        setAuthError('Failed to send reset email. Please try again.')
      }
      return false
    }
  }

  const sendEmailLink = async (email) => {
    setAuthError()
    try {
      // The backend validates the email, generates the sign-in link, and
      // sends it via SES SMTP. Store the email locally so the callback can
      // complete sign-in regardless of whether the email was associated.
      window.localStorage.setItem('emailForSignIn', email)
      const sendLink = httpsCallable(getFunctions(), 'checkCustomerEmail')
      await sendLink({ email, continueUrl: window.location.origin })
      return true
    } catch (err) {
      setAuthError('Failed to send sign-in link. Please try again.')
      return false
    }
  }

  const completeEmailLinkSignIn = async (url) => {
    const auth = getAuth()
    if (!isSignInWithEmailLink(auth, url)) return false
    // Only attempt once — the oobCode is single-use.
    if (emailLinkSignInAttempted) return false
    emailLinkSignInAttempted = true
    setAuthError()
    try {
      let email = window.localStorage.getItem('emailForSignIn')
      if (!email) {
        email = window.prompt('Please provide your email for confirmation')
      }
      if (!email) return false
      const result = await signInWithEmailLink(auth, email, url)
      window.localStorage.removeItem('emailForSignIn')
      // Strip the one-time sign-in params from the URL so a refresh doesn't retry.
      if (window.history?.replaceState) {
        window.history.replaceState({}, document.title, window.location.pathname)
      }
      setUser(result.user)
      return true
    } catch (err) {
      if (err.code === 'auth/invalid-action-code') {
        setAuthError('This sign-in link has expired or already been used.')
      } else {
        setAuthError('Failed to complete sign-in. Please try again.')
      }
      return false
    }
  }

  // Keep backward compat — login defaults to Google
  const login = loginWithGoogle

  const logout = () => {
    signOut(getAuth()).then(() => {
      setUser(undefined)
    })
  }

  return {
    loading,
    user,
    login,
    loginWithGoogle,
    loginWithEmail,
    sendEmailLink,
    completeEmailLinkSignIn,
    register,
    resetPassword,
    logout,
    authError,
    setAuthError
  }
}

export default useAuthHook
